/**
 * Reading the audit trail.
 *
 * Forward-only pagination, because the trail is paged by cursor and that is
 * not a limitation to work around — it is the reason the trail can be trusted.
 * With `OFFSET`, entries arriving at the top while somebody reads page two
 * push records across the boundary: they see one twice and never see another.
 * In an audit trail, "never see another" is the failure that matters.
 *
 * So there is no "page 7" to jump to, and the interface does not pretend
 * otherwise.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiClient } from "@hardauto/core-api";
import {
  Badge,
  Button,
  DataTable,
  Input,
  Page,
  Pagination,
  type BadgeTone,
  type Column,
} from "@hardauto/core-ui";
import { Field } from "@hardauto/core-forms";

import { AdminApi, type AuditEntry } from "../api";
import { NAMESPACE } from "../i18n";

const PAGE_SIZE = 50;

const RESULT_TONE: Record<string, BadgeTone> = {
  success: "success",
  failure: "danger",
  denied: "warning",
};

export function AuditScreen({ client }: { client: ApiClient }) {
  const { t } = useTranslation(NAMESPACE);
  const api = useMemo(() => new AdminApi(client), [client]);

  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursors, setCursors] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  // Separate from the applied filters: typing in the box must not refetch on
  // every keystroke, and an audit query is expensive enough that it would be
  // felt.
  const [draftAction, setDraftAction] = useState("");
  const [draftResource, setDraftResource] = useState("");
  const [filters, setFilters] = useState<{ action?: string; resource_type?: string }>({});

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const page = await api.readAudit({
        ...filters,
        limit: PAGE_SIZE,
        before_id: cursors.at(-1),
      });
      setEntries(page.entries);
      setCursor(page.cursor);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [api, filters, cursors]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: Column<AuditEntry>[] = [
    {
      key: "when",
      header: t("audit.when"),
      render: (entry) => new Date(entry.occurred_at).toLocaleString(),
    },
    {
      key: "who",
      header: t("audit.who"),
      // Null means the system did it — a scheduled job expiring a session, a
      // migration seeding a role — and "—" would read as missing data.
      render: (entry) => entry.actor_user_id ?? t("audit.system"),
    },
    { key: "action", header: t("audit.action"), render: (entry) => <code>{entry.action}</code> },
    {
      key: "resource",
      header: t("audit.resource"),
      secondary: true,
      render: (entry) =>
        entry.resource_type ? `${entry.resource_type} ${entry.resource_id ?? ""}` : "—",
    },
    {
      key: "result",
      header: t("audit.result"),
      render: (entry) => (
        <Badge tone={RESULT_TONE[entry.result] ?? "neutral"}>{entry.result}</Badge>
      ),
    },
    {
      key: "details",
      header: "",
      align: "end",
      render: (entry) =>
        Object.keys(entry.before).length + Object.keys(entry.after).length > 0 ? (
          // A native <details>, so it is keyboard-operable and announced
          // without a line of JavaScript.
          <details>
            <summary>{t("audit.details")}</summary>
            <pre>{JSON.stringify({ before: entry.before, after: entry.after }, null, 2)}</pre>
          </details>
        ) : null,
    },
  ];

  return (
    <Page title={t("audit.title")} description={t("audit.description")}>
      <form
        className="hardauto-admin-filters"
        onSubmit={(event) => {
          event.preventDefault();
          setCursors([]);
          setFilters({
            ...(draftAction ? { action: draftAction } : {}),
            ...(draftResource ? { resource_type: draftResource } : {}),
          });
        }}
      >
        <Field label={t("audit.filterAction")}>
          {(props) => (
            <Input
              {...props}
              value={draftAction}
              onChange={(event) => {
                setDraftAction(event.target.value);
              }}
            />
          )}
        </Field>

        <Field label={t("audit.filterResource")}>
          {(props) => (
            <Input
              {...props}
              value={draftResource}
              onChange={(event) => {
                setDraftResource(event.target.value);
              }}
            />
          )}
        </Field>

        <Button type="submit" variant="primary">
          {t("audit.apply")}
        </Button>
        <Button
          onClick={() => {
            setDraftAction("");
            setDraftResource("");
            setCursors([]);
            setFilters({});
          }}
        >
          {t("audit.clear")}
        </Button>
      </form>

      <DataTable<AuditEntry>
        caption={t("audit.caption")}
        columns={columns}
        rows={entries}
        // The trail has no surrogate key on the wire, and two entries can share
        // a timestamp, so the correlation id and action are folded in to keep
        // the key unique.
        rowKey={(entry) => `${entry.occurred_at}-${entry.correlation_id ?? ""}-${entry.action}`}
        loading={loading}
        error={
          failed
            ? {
                title: t("audit.loadFailed"),
                onRetry: () => void load(),
                retryLabel: t("error.retry"),
              }
            : undefined
        }
        empty={{ title: t("audit.empty"), description: t("audit.emptyDetail") }}
        loadingLabel={t("audit.loading")}
      />

      <Pagination
        previousLabel={t("audit.previous")}
        nextLabel={t("audit.next")}
        busy={loading}
        onPrevious={
          cursors.length > 0
            ? () => {
                setCursors((stack) => stack.slice(0, -1));
              }
            : undefined
        }
        onNext={
          cursor
            ? () => {
                setCursors((stack) => [...stack, cursor]);
              }
            : undefined
        }
      />
    </Page>
  );
}
